import { useEffect, useMemo, useRef, useState } from 'react';
import { chooseBackend, memoryBackend, isRuntimeBrowser } from '../helpers/storageAdapters';
import { safeParse, safeStringify } from '../helpers/jsonSafe';

export function useStorage(kind, key, defaultValue, { syncAcrossTabs = true } = {}) {
  const backend = useMemo(() => {
    if (!isRuntimeBrowser) return memoryBackend;
    return chooseBackend(kind);
  }, [kind]);

  const initial = useMemo(() => {
    if (!isRuntimeBrowser)
      return typeof defaultValue === 'function' ? defaultValue() : defaultValue;
    try {
      const raw = backend.getItem?.(key);
      if (raw != null)
        return safeParse(raw, typeof defaultValue === 'function' ? defaultValue() : defaultValue);

      const toStore = safeStringify(
        typeof defaultValue === 'function' ? defaultValue() : defaultValue
      );
      if (toStore !== undefined) backend.setItem?.(key, toStore);
      return typeof defaultValue === 'function' ? defaultValue() : defaultValue;
    } catch {
      return typeof defaultValue === 'function' ? defaultValue() : defaultValue;
    }
  }, [backend, key, defaultValue]);

  const [value, setValue] = useState(initial);
  const valueRef = useRef(value);
  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    if (!isRuntimeBrowser || !syncAcrossTabs) return;
    const isRealWebStorage =
      typeof backend === 'object' &&
      ('key' in backend || backend === window.localStorage || backend === window.sessionStorage);
    if (!isRealWebStorage) return;

    function onStorage(e) {
      if (e.key !== key) return;

      const next =
        e.newValue != null
          ? safeParse(e.newValue, valueRef.current)
          : typeof defaultValue === 'function'
            ? defaultValue()
            : defaultValue;
      setValue(next);
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [backend, key, defaultValue, syncAcrossTabs]);

  const setStored = (updater) => {
    const next = typeof updater === 'function' ? updater(valueRef.current) : updater;
    setValue(next);
    if (!isRuntimeBrowser) return;

    try {
      const asJson = safeStringify(next);

      if (asJson === undefined) return;
      backend.setItem?.(key, asJson);
    } catch (e) {
      try {
        memoryBackend.setItem(key, safeStringify(next));
      } catch {}
    }
  };

  const clear = () => {
    setValue(typeof defaultValue === 'function' ? defaultValue() : defaultValue);
    if (!isRuntimeBrowser) return;
    try {
      backend.removeItem?.(key);
    } catch {}
  };

  return [value, setStored, clear];
}
